import { SENTRY_DSN } from '@/env'
import { initPostHog, scrubRedemptionUrls } from '@/features/analytics/posthog'
import { captureCampaignClaim } from '@/features/nectar/api/campaignClaim'
import { capturePayPalCallbackParams } from '@/features/wallet/api'
import { detectInitialLocale, dynamicActivate } from '@/i18n'
import '@/utils/dayjs'
import { isMockHarness } from '@/utils/mockHarness'
import { captureUtmParams } from '@/utils/utmParams'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import { App } from './App'

// Dev-only mock harness (A0). MUST be the first statement in the file: captureCampaignClaim()
// below can fetch, so anything started later risks a request escaping before the worker intercepts.
// `import.meta.env.DEV` is replaced with `false` in a build, so this whole block is tree-shaken out.
// The same flag keeps PostHog and GTM off, so the harness's fake events never reach a real project.
if (isMockHarness()) {
  const { worker } = await import('@/test/msw/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

// MUST run before PostHog/Sentry init — strips ?code= from the PayPal consent callback URL so
// neither SDK ever captures the authorization code in a pageview/pageload event (IV3).
capturePayPalCallbackParams()
initPostHog()
captureUtmParams()
captureCampaignClaim()

if (window.location.hostname !== 'localhost') {
  Sentry.init({
    allowUrls: [/https?:\/\/(dashboard|api)\.datahive\.ai/],
    denyUrls: [/chrome-extension:\/\//i, /moz-extension:\/\//i],
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/datahive\.ai\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // IV-31 — redact the Tremendous redemption URL (permanent bearer authority) from events and
    // breadcrumbs. Defence in depth: these hooks do NOT see Session Replay payloads (replay masks
    // text and input values but not attributes — hence the URL is never put in the DOM), and
    // nothing here reaches Google Tag Manager.
    beforeSend: (event) => scrubRedemptionUrls(event, 0) as typeof event,
    beforeBreadcrumb: (breadcrumb) => scrubRedemptionUrls(breadcrumb, 0) as typeof breadcrumb
  })
}

const appContainer = document.getElementById('app')
if (appContainer) {
  void dynamicActivate(detectInitialLocale()).then(() => {
    ReactDOM.createRoot(appContainer).render(<App />)
  })
}
